import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { surface, border, textPrimary, textSecondary, font, shadow, accent } from "../../../layouts/schoolPortal/schoolPortalTheme";
import { useSchool } from "../../../Hooks/SchoolContext";
import {
  create_connect_onboarding_link_api, get_connect_status_api,
  create_school_connect_onboarding_link_api, get_school_connect_status_api,
} from "../../../DAL/SchoolPortal/Tutors";

/**
 * Landing page for both Stripe Connect return_url/refresh_url targets
 * (instructor payouts in connect_onboarding.js, school payouts in
 * school_connect_onboarding.js) — Stripe redirects the browser here after
 * the hosted onboarding flow, with ?type=instructor|school distinguishing
 * which account it was, and ?complete=true or ?refresh=true for why we're here.
 * Without this page the redirect landed on a route that didn't exist (blank page).
 *
 * Where they go next comes from ?returnTo, which whichever page started the
 * flow supplied — there is no single right answer, because a school admin
 * starts in Settings, a school's instructor in Teaching, and an independent
 * instructor on their dashboard checklist. The fallbacks below only apply to
 * links minted before returnTo existed, or if the value failed validation.
 */

// Mirrors the server's allowlist (utils/safe_return_path.js). Checked again
// here because this value came back through a third party's redirect, and a
// path that arrives via Stripe is not more trustworthy than one that doesn't.
const SAFE_PATH = /^[A-Za-z0-9/_.?=&-]+$/;
const safeReturnTo = (value, fallback) => {
  if (!value || typeof value !== "string") return fallback;
  if (value.length > 200) return fallback;
  if (!value.startsWith("/school/")) return fallback;
  if (!SAFE_PATH.test(value) || value.includes("..")) return fallback;
  return value;
};

export default function ConnectOnboardingReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { isIndependentInstructor } = useSchool();
  const [status, setStatus] = useState("working"); // working | done
  const ran = useRef(false);

  const type = params.get("type") === "school" ? "school" : "instructor";
  const isRefresh = params.get("refresh") === "true";

  // An independent instructor's home is the dashboard; everyone else's depends
  // on which account they were setting up.
  const fallback = type === "school"
    ? "/school/settings"
    : (isIndependentInstructor ? "/school/dashboard" : "/school/teaching");
  const destination = safeReturnTo(params.get("returnTo"), fallback);

  const getLink = type === "school" ? create_school_connect_onboarding_link_api : create_connect_onboarding_link_api;
  const getStatus = type === "school" ? get_school_connect_status_api : get_connect_status_api;

  useEffect(() => {
    if (ran.current) return; // StrictMode double-invoke guard — this makes a real Stripe call
    ran.current = true;
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async () => {
    if (isRefresh) {
      // The account link Stripe gave us expired before the instructor/school
      // finished — get a fresh one and send them straight back, no dead end.
      // Pass the origin on, or the second attempt lands somewhere else.
      const linkRes = await getLink(destination);
      if (linkRes.code === 200 && linkRes.data?.url) {
        window.location.href = linkRes.data.url;
        return;
      }
      enqueueSnackbar("Couldn't resume Stripe setup — you can restart it from here.", { variant: "error" });
      setStatus("done");
      setTimeout(() => navigate(destination, { replace: true }), 1200);
      return;
    }

    // Returned from (or abandoned) Stripe's flow. account.charges_enabled can
    // lag the redirect by a second or two, so poll once, wait, then re-check
    // before giving up and just sending them back either way.
    let res = await getStatus();
    if (res.code === 200 && !res.data?.onboardingComplete) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      res = await getStatus();
    }

    if (res.code === 200 && res.data?.onboardingComplete) {
      enqueueSnackbar("Payouts connected — you're all set.", { variant: "success" });
    } else {
      enqueueSnackbar("Stripe still needs a bit more information to finish setup.", { variant: "warning" });
    }
    setStatus("done");
    navigate(destination, { replace: true });
  };

  return (
    <Box sx={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
      <Box sx={{ backgroundColor: surface, border: `1px solid ${border}`, borderRadius: "14px", boxShadow: shadow, p: 4, textAlign: "center", maxWidth: 360 }}>
        <CircularProgress size={28} sx={{ color: accent, mb: 2 }} />
        <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "15px", color: textPrimary, mb: 0.5 }}>
          {isRefresh ? "Resuming Stripe setup…" : "Finishing your payout setup…"}
        </Typography>
        <Typography sx={{ fontFamily: font, fontSize: "13px", color: textSecondary }}>
          {status === "working" ? "This only takes a moment." : "Redirecting you now…"}
        </Typography>
      </Box>
    </Box>
  );
}
